import React from 'react'
import { Header, Text } from '@crossfit/ui'

import type { Image } from 'types'
import InnerContent from 'components/InnerContent'
import style from './style.module.css'
import { trackEvent } from 'services/segment'
import { useAffiliate } from 'hooks/useAffiliate'

type Partner = {
  headlineText: string
  bodyText: string
  category: string
  image: Image
  countryEligibility: string[]
  unavailableLink: {
    text: string
    url: string
  }
  link: {
    text: string
    url: string
  }
}

export function PartnerItem({
  partner,
  unavailable,
}: {
  partner: Partner
  unavailable?: boolean
}) {
  const link = unavailable ? partner.unavailableLink : partner.link
  const { selectedAffiliate } = useAffiliate()
  const onClick = () => {
    trackEvent({
      event: 'Partner Network Offer Clicked',
      component: 'Partner Network Page',
      location: 'Affiliate Toolkit',
      linkUrl: link.url,
      text: partner.link.text,
      tileName: partner.headlineText,
      affiliateId: selectedAffiliate?.affiliateId,
      userRole:
        selectedAffiliate?.roles?.length > 0
          ? selectedAffiliate?.roles[0]
          : null,
      groupId: selectedAffiliate?.groupId,
    })
    location.href = link.url
  }
  return (
    <div key={partner.headlineText} className={style.partner}>
      <img
        src={partner.image.sizes.large}
        alt={partner.image.alt}
        width={partner.image.sizes.largeWidth}
        height={partner.image.sizes.largeHeight}
      />
      <div className={style.content}>
        <Header as='h4' size='small' className={style.headlineText}>
          {partner.headlineText}
        </Header>
        <Text className={style.category}>{partner.category}</Text>
        <Text className={style.bodyText}>{partner.bodyText}</Text>
        <button
          className={style.link}
          aria-label={`${link.text} for ${partner.headlineText}`}
          onClick={() => onClick()}
        >
          {link.text}
        </button>
      </div>
    </div>
  )
}

type Props = {
  headerText: string
} & React.ComponentPropsWithoutRef<'div'>

function PartnersList({ headerText, children }: Props) {
  return (
    <InnerContent variant='xlarge'>
      <Header as='h3' size='small' mb={8} className={style.header}>
        {headerText}
      </Header>

      <ul className={style.partners}>
        {React.Children.map(
          children,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (child: React.ReactElement<any, string>) => (
            <li>{React.cloneElement(child)}</li>
          ),
        )}
      </ul>
    </InnerContent>
  )
}

export default PartnersList
